import React from 'react';
import { CircleDollarSign, Clock, ExternalLink } from 'lucide-react';
import { useTenantNavigate } from '../../services/hooks';

interface PagosPendientesProps {
    pagos: {
        id: string;
        clienteNombre: string;
        suscripcion_id: string;
        importe: number;
        metodoPago: string;
        fechaVencimiento: string;
    }[];
    isLoading: boolean;
}

const PagosPendientes: React.FC<PagosPendientesProps> = ({ pagos, isLoading }) => {
    const navigate = useTenantNavigate();
    
    // Nombres de los meses en español
    const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    const mesActual = meses[new Date().getMonth()];
    const anioActual = new Date().getFullYear();
    
    const handlePagoClick = (suscripcion_id: string) => {
        navigate('/clientes/otros-pagos');
    };
    
    return (
        <div className="bg-white rounded-lg shadow-md p-4 h-full">
            <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center">
                <CircleDollarSign className="mr-2" size={18} />
                Pagos pendientes ({mesActual} {anioActual})
                {pagos.length > 0 && (
                    <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                        {pagos.length}
                    </span>
                )}
            </h3>
            
            {isLoading ? (
                <div className="flex justify-center items-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
            ) : pagos.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay pagos pendientes para este mes</p>
            ) : (
                <div className="overflow-y-auto max-h-64">
                    <ul className="space-y-2">
                        {pagos.map((pago, index) => (
                            <li 
                                key={index} 
                                className="flex items-start border-b border-gray-100 pb-2 cursor-pointer hover:bg-gray-50 rounded p-1 transition-colors"
                                onClick={() => handlePagoClick(pago.suscripcion_id)}
                            >
                                <div className="bg-red-100 text-red-800 rounded-full p-1 mr-2">
                                    <Clock size={16} />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-sm">{pago.clienteNombre}</p>
                                    <div className="flex justify-between items-center">
                                        <p className="text-xs text-gray-600">Vence: {pago.fechaVencimiento}</p>
                                        <p className="text-sm font-bold text-red-600">{pago.importe.toFixed(2)}€</p>
                                    </div>
                                    <p className="text-xs text-gray-500 italic">Método: {pago.metodoPago}</p>
                                </div>
                                <div className="ml-2 text-blue-600">
                                    <ExternalLink size={14} />
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            
            {pagos.length > 0 && (
                <button 
                    onClick={() => navigate('/clientes/otros-pagos')}
                    className="mt-3 w-full text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center"
                >
                    Ver todos los pagos
                </button>
            )}
        </div>
    );
};

export default PagosPendientes;